import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import ExerciseFlowSteps from "../components/ExerciseFlowSteps";
import SeoHead from "../components/SeoHead";
import { muscleGroups } from "../data/muscleGroups";
import { createCustomExercise } from "../services/customExerciseService";
import { pullAndMergeExerciseLog, pushExerciseLog } from "../services/syncService";
import { useAuthCompat } from "../utils/clerkConfig";
import { useI18n } from "../i18n/i18n";
import { trackEvent } from "../utils/analytics";
import "./CreateExercise.css";
import "./SelectExercise.css";
import "../styles/Common.css";

export default function CreateExercise() {
    const { t, localizePath } = useI18n();
    const location = useLocation();
    const navigate = useNavigate();
    const { getToken } = useAuthCompat();
    const [exerciseName, setExerciseName] = useState(() => {
        const params = new URLSearchParams(location.search || "");
        return params.get("name") || "";
    });
    const [selectedMuscleGroup, setSelectedMuscleGroup] = useState("");
    const [saveError, setSaveError] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const muscleOptions = useMemo(
        () => muscleGroups.map((group) => ({
            id: group.id,
            name: t(`muscleGroups.${group.id}`) || group.name,
        })),
        [t]
    );

    const handleSave = async () => {
        const trimmedName = exerciseName.trim();

        if (!trimmedName) {
            setSaveError(t("createExercise.nameRequired"));
            return;
        }

        if (!selectedMuscleGroup) {
            setSaveError(t("createExercise.muscleGroupRequired"));
            return;
        }

        setIsSaving(true);
        setSaveError("");

        try {
            try {
                await pullAndMergeExerciseLog(getToken);
            } catch (_) {
                // Keep local create flow working even when remote sync is unavailable.
            }

            const exercise = createCustomExercise({
                name: trimmedName,
                muscleGroup: selectedMuscleGroup,
            });

            trackEvent("create_exercise", {
                exercise_id: exercise.id,
                muscle_group: selectedMuscleGroup,
            });

            try {
                await pushExerciseLog(getToken);
            } catch (_) {
                // Local save is already complete; background sync is best-effort.
            }

            navigate(localizePath(`/LogExercise/${exercise.id}`));
        } catch (error) {
            if (error?.message === "DUPLICATE_CUSTOM_EXERCISE") {
                setSaveError(t("createExercise.duplicateName"));
            } else {
                setSaveError(t("createExercise.saveFailed"));
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="selectExercisePage createExercisePage pageContainer">
            <SeoHead
                title={t("createExercise.seoTitle")}
                description={t("createExercise.seoDescription")}
                canonicalPath={localizePath("/CreateExercise")}
                noIndex={true}
            />
            <Breadcrumbs current={t("createExercise.title")} />
            <Link to={localizePath("/")} className="flowBackLink appTextLink">
                <span className="flowBackLinkIcon" aria-hidden="true">←</span>
                <span className="flowBackLinkLabel">{t("common.back")}</span>
            </Link>
            <ExerciseFlowSteps activeStep={1} />
            <h1 className="sectionHeading">{t("createExercise.title")}</h1>

            <div className="createExerciseForm">
                <label className="createExerciseField">
                    <span className="createExerciseLabel">{t("createExercise.nameLabel")}</span>
                    <input
                        type="text"
                        value={exerciseName}
                        onChange={(event) => setExerciseName(event.target.value)}
                        className="searchInput"
                        placeholder={t("createExercise.namePlaceholder")}
                        maxLength={120}
                    />
                </label>

                <div className="createExerciseField">
                    <div className="createExerciseLabel">{t("createExercise.primaryMuscleGroup")}</div>
                    <div className="filterContainer isExpanded">
                        <div className="filterGroup">
                            <div className="muscleOptions" aria-label={t("createExercise.primaryMuscleGroup")}>
                                {muscleOptions.map((option) => (
                                    <label key={option.id} className="muscleOption">
                                        <input
                                            type="radio"
                                            name="primaryMuscleGroup"
                                            value={option.id}
                                            checked={selectedMuscleGroup === option.id}
                                            onChange={(event) => setSelectedMuscleGroup(event.target.value)}
                                        />
                                        <span className="muscleOptionLabel">{option.name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {saveError && (
                    <div className="createExerciseError">
                        <strong>{t("createExercise.errorPrefix")}</strong> {saveError}
                    </div>
                )}

                <button
                    type="button"
                    className="appButton createExerciseSaveButton"
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? t("createExercise.saving") : t("createExercise.save")}
                </button>
            </div>
        </div>
    );
}
